import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Header, Icon, Image } from 'react-frontier';
import { CardBrand, PaymentData } from '../PayClasses';
import PayloadSend, { PayloadSendElement } from './PayloadSend';
import API, { CDN_URL } from '../PublicAPI';
import classNames from 'classnames';
import '../style/charge.scss';
import { addCommas, isJWT } from '../PublicUtil';

var PaymentContainer = (props: PropsWithChildren & { clientId: number })=>{
	return <div className="pf charge-screen">
		<div className="client">
			{props.clientId ? (
				<Image src={`${CDN_URL}/clients/${props.clientId}.webp`} fallbackComponent={(
					<div className='placeholder' />
				)} />
			) : (
				<div className='placeholder' />
			)}
		</div>
		<div className="contents">
			{props.children}
		</div>
		<div className="powered">Powered by Payfront</div>
	</div>
}

interface PaymentChargeProps{
	error?: boolean
}

const WAIT_INTERVAL = 5000;

var PaymentCharge = (props: PaymentChargeProps)=>{
	var { t } = useTranslation();
	var { hash } = useParams();
	var [searchParams] = useSearchParams();
	var [loadError, setLoadError] = useState<string>(null);
	var [payment, setPayment] = useState<PaymentData>(null);
	var [loadingContinue, setLoadingContinue] = useState<boolean>(false);
	var [listening, setListening] = useState<boolean>();
	var redirectRef = useRef<PayloadSendElement>(null);

	var hash_valid = useMemo(()=>{
		return isJWT(hash);
	}, [hash]);

	useEffect(()=>{
		if(!hash_valid || props.error || !!payment) return;
		localStorage.setItem('payment', hash);
		API.getPayment(hash).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayment(res.data);
			if(!res.data.charging && !res.data.paid && !res.data.cancelled){
				if(res.data.redirect){

				}else if(res.data.post_url){

				}else{
					sendCharge();
				}
			}else if(res.data.charging){
				startSocket();
			}
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PFP-PYCHGP-1' }));
		})
	}, []);

	useEffect(()=>{
		if(!listening || !hash_valid) return;

		var tm = setInterval(()=>{
			API.getPaymentStatus(hash).then(res=>{
				if(res.error) return setLoadError(res.message);
				setPayment({
					...payment,
					paid: res.data.paid,
					cancelled: res.data.cancelled,
					cancel_reason: res.data.cancel_reason,
					redirect: res.data.redirect,
				});
				if(res.data.paid || res.data.cancelled) setListening(false);
			}).catch(err=>{
				setLoadError(t('common.unexpected_error', { code: 'PFP-PYSTS-1' }));
			});
		}, Math.max((payment?.interval || WAIT_INTERVAL), WAIT_INTERVAL));

		return ()=>{
			clearInterval(tm);
		}
	}, [listening, payment]);

	var client_id = useMemo(()=>{
		if(!payment){
			var clpr = searchParams.get('c');
			if(!clpr) return null;
			var numclpr = parseInt(clpr);
			if(Number.isNaN(numclpr)) return null;
			return numclpr;
		}else{
			return payment.client_id;
		}
	}, [payment]);

	var sendCharge = ()=>{
		API.sendCharge(hash).then(res=>{
			if(res.error) return setLoadError(res.message);
			if(res.data.paid || res.data.cancelled){
				setPayment({
					...payment,
					paid: res.data.paid,
					cancelled: res.data.cancelled,
				})
				return;
			}
			startSocket();
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PFP-PYCHHG-1' }));
		})
	}

	var startSocket = ()=>{
		setListening(true);
	}

	var continueMerchant = ()=>{
		if(redirectRef.current){
			redirectRef.current.submit()
		}
	}

	var show_header = (!hash_valid || loadError || payment?.paid || payment?.cancelled);
	var brand_logo = useMemo(()=>{
		if(!payment?.card?.card_brand) return null;
		switch(payment.card.card_brand){
			case CardBrand.VISA: return 'visa';
			case CardBrand.MASTERCARD: return 'mc';
			case CardBrand.AMERICAN_EXPRESS: return 'amex';
			default: return null;
		}
	}, [payment]);

	var cancel_reason_text = useMemo(()=>{
		if(!payment || !payment.cancel_reason) return null;
		return t(`cancel_reason.reason_${payment.cancel_reason || 1}`, { defaultValue: t('cancel_reason.generic') });
	}, [payment?.cancel_reason]);
	
	return <PaymentContainer clientId={client_id}>
		<div className={classNames("card", { icon: show_header })}>
			{show_header? (
				<div className={classNames("container", {
					error: !hash_valid || loadError || payment?.cancelled,
					success: payment?.paid
				})}>
					<Icon name={payment?.paid ? 'check' : 'times'} />
				</div>
			) : <>
				<div className="top">
					{brand_logo ? (
						<img src={`/assets/${brand_logo}.svg`} className='brand' />
					) : (
						<div className="brand placeholder"></div>
					)}
					<div className="fr inline loading"></div>
				</div>
				<div className="bottom">
					<div className="status">Procesando pago</div>
					<div className={classNames("number", { placeholder: !payment?.card?.card_last4 })}>
						<div className="placeholder"></div>
						<div className="placeholder"></div>
						<div className="placeholder"></div>
						{payment && payment.card?.card_last4 ? (
							<div className="text">{payment.card.card_last4}</div>
						) : (
							<div className="placeholder"></div>
						)}
					</div>
				</div>
			</>}
		</div>
		{show_header && (
			!hash_valid ? (
				searchParams.get('ec') ? (
					<Header text={'Error'} subtext={`Hubo un error realizando la autenticación del pago. No se realizó ningún cobro (${searchParams.get('ec')})`} />
				) : (
					<Header text={'Error'} subtext={'El enlace de pago no es válido'} />
				)
			) : loadError ? (
				<Header text={'Error'} subtext={loadError} />
			) : payment?.paid ? (
				null
			) : payment?.cancelled ? (
				<Header text={cancel_reason_text} subtext={t('charge.not_charged')} actions={(
					payment.redirect ? (
						<Button text='Regresar' loading={loadingContinue} color='black' onClick={continueMerchant} />
					) : null
				)} />
			) : null
		)}
		{(!show_header || !!payment?.paid) && (
			<div className="amount">
				<div className="label">{t(`charge.${payment?.paid ? 'paid' : 'paying'}`)}</div>
				<div className={classNames("value", {
					placeholder: !payment,
				})}>
					{!!payment ? (
						addCommas(payment.total)
					) : null}
				</div>
				<div className={classNames("meta", {
					placeholder: !payment?.currency
				})} style={{ marginTop: !!payment ? -5 : 0 }}>
					{payment?.currency}
				</div>
			</div>
		)}
		{payment && payment.paid && payment.redirect && (
			<div style={{ textAlign: 'center' }}>
				<Button text='Continuar' loading={loadingContinue} color='black' onClick={continueMerchant} style={{ width: 150, marginBottom: 10 }} />
			</div>
		)}
		{!!payment && !!payment.payload && (
			<PayloadSend method={(payment.payload.method as any) || 'POST'} url={payment.payload.url} data={payment.payload.data} autoSubmit />
		)}
		{!!payment && payment.redirect && (
			<PayloadSend autoSubmit ref={redirectRef} onSubmit={()=>setLoadingContinue(true)} submitTimeout={payment.paid ? 2000 : 5000} url={payment.redirect} data={{
				payment_id: payment.payment_id,
				paid: !!payment.paid ? 1 : 0,
				total: payment.total,
				cancel_reason: payment.cancel_reason,
				message: payment.paid ? t('charge.payment_paid') : cancel_reason_text,
				cancelled: !!payment.cancelled ? 1 : 0,
			}} />
		)}
	</PaymentContainer>
}

export default PaymentCharge;